import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import type { SignalBriefVerdict } from "../../api/client.ts";
import AgentRunStatusIcon from "../agent/AgentRunStatusIcon.tsx";
import { statusStyle } from "../../utils/statusColors.ts";
import { isJobActive } from "../../utils/status.ts";

// The checks a research verdict follows from. Mirrors
// SIGNAL_BRIEF_ASSESSMENT_GATES in svc-backend db/models/signals.py.
export const ASSESSMENT_GATES: { key: string; label: string; question: string }[] = [
  {
    key: "event_confirmed",
    label: "Event confirmed",
    question: "Is the event real and as the record describes it?",
  },
  {
    key: "matches_criteria",
    label: "Matches criteria",
    question: "Does it fit what this workflow is looking for?",
  },
  {
    key: "new_trigger",
    label: "New trigger",
    question: "Is it new, not a stale recap or a look back with no new finding?",
  },
  {
    key: "addressable_buyer",
    label: "Addressable buyer",
    question: "Is there an organisation to approach that this workflow sells to?",
  },
];

export const VERDICTS: { value: SignalBriefVerdict; label: string; hint: string }[] = [
  {
    value: "verified",
    label: "Verified",
    hint: "Every check passed: the event is confirmed, fits the workflow, is a new trigger and has a buyer to approach.",
  },
  {
    value: "qualified",
    label: "Qualified",
    hint: "No check failed, but some are unclear. Those are its caveats. Also used when the agent holds back from verified.",
  },
  {
    value: "disqualified",
    label: "Disqualified",
    hint: "A check failed: not what the workflow looks for, a stale story, or no buyer to approach.",
  },
  {
    value: "inconclusive",
    label: "Inconclusive",
    hint: "The event could not be confirmed, or too little could be established.",
  },
  {
    value: "duplicate",
    label: "Duplicate",
    hint: "Same event as another signal that was researched. Hidden unless filtered for.",
  },
];

/**
 * The research agent's verdict on a signal; while it works (or when it failed
 * without one), the run's status icon.
 */
export default function ResearchVerdictChip({
  verdict,
  status,
  confidence,
}: {
  verdict: SignalBriefVerdict | null;
  status?: string | null;
  confidence?: number | null;
}) {
  if (isJobActive(status) || (!verdict && status === "failed")) {
    return <AgentRunStatusIcon status={status ?? "running"} agentName="The research agent" />;
  }
  if (!verdict) return null;
  const meta = VERDICTS.find((v) => v.value === verdict);
  const title = confidence ? `${meta?.hint} Research confidence ${confidence}/5.` : meta?.hint;
  return (
    <Tooltip title={title ?? ""}>
      <Chip
        size="small"
        color={statusStyle("research_verdict", verdict).color}
        label={meta?.label ?? verdict}
      />
    </Tooltip>
  );
}
